import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';

const STORAGE_PREFIX = 'cap4:modal-size:';
const BASELINE_PREFIX = 'cap4:modal-size-baseline:';
const HINT_KEY = 'cap4:modal-resize-hint-shown';

export interface ResizableModalProps extends HTMLAttributes<HTMLDivElement> {
  storageKey: string;
  children: ReactNode;
  onClose?: () => void;
  /** Async guard called before close — return false to prevent closing */
  closeGuard?: () => Promise<boolean>;
  /** Text shown on first-time resize hint below the modal */
  resizeHintText?: string;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: string | number;
  maxHeight?: string | number;
  disableOnMobile?: boolean;
}

export function ResizableModal({
  storageKey,
  className,
  style,
  children,
  onClose,
  closeGuard,
  resizeHintText = 'Drag corner to resize · double-click to reset',
  minWidth = 520,
  minHeight = 360,
  maxWidth = 'calc(100vw - 2rem)',
  maxHeight = 'calc(100vh - 2rem)',
  disableOnMobile = true,
  ...rest
}: ResizableModalProps) {
  const {
    role = 'dialog',
    'aria-modal': ariaModal = true,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    ...innerRest
  } = rest;
  const ref = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef<{
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
  } | null>(null);
  const isDraggingRef = useRef(false);
  const hasUserSizedRef = useRef(false);
  const suppressClickRef = useRef(false);
  const latestSizeRef = useRef<{ width: number; height: number } | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);
  const [showHint, setShowHint] = useState(false);
  const [showScrollHint, setShowScrollHint] = useState(false);
  const storedSizeRef = useRef<{ width: number; height: number } | null>(null);
  const initialSizeRef = useRef<{ width: number; height: number } | null>(null);
  const baselineSizeRef = useRef<{ width: number; height: number } | null>(null);
  const hasMeasuredRef = useRef(false);
  const lastMeasuredRef = useRef<{ width: number; height: number } | null>(null);
  const resetPendingRef = useRef(false);
  const hasUnsavedRef = useRef(false);

  useEffect(() => {
    latestSizeRef.current = size;
  }, [size]);

  useEffect(() => {
    const handleClickCapture = (event: MouseEvent) => {
      if (!suppressClickRef.current) return;
      suppressClickRef.current = false;
      event.preventDefault();
      event.stopPropagation();
    };
    window.addEventListener('click', handleClickCapture, true);
    return () => window.removeEventListener('click', handleClickCapture, true);
  }, []);

  useEffect(() => {
    if (!onClose) return;
    const handleKey = async (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const allModals = document.querySelectorAll('[data-resizable-modal-root]');
      if (allModals.length === 0) return;
      const topmost = allModals[allModals.length - 1];
      if (ref.current !== topmost) return;
      if (closeGuard && !(await closeGuard())) return;
      onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose, closeGuard]);

  const storageId = useMemo(() => `${STORAGE_PREFIX}${storageKey}`, [storageKey]);
  const baselineId = useMemo(() => `${BASELINE_PREFIX}${storageKey}`, [storageKey]);

  useEffect(() => {
    if (!disableOnMobile) {
      setEnabled(true);
      return;
    }
    const media = window.matchMedia('(min-width: 768px)');
    const update = () => setEnabled(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, [disableOnMobile]);

  useEffect(() => {
    if (enabled) return;
    latestSizeRef.current = null;
    storedSizeRef.current = null;
    baselineSizeRef.current = null;
    initialSizeRef.current = null;
    lastMeasuredRef.current = null;
    hasMeasuredRef.current = false;
    hasUserSizedRef.current = false;
    resetPendingRef.current = false;
    setSize((current) => (current ? null : current));
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    try {
      const seen = window.localStorage.getItem(HINT_KEY);
      if (!seen) setShowHint(true);
    } catch { /* localStorage unavailable */ }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    try {
      const raw = window.localStorage.getItem(storageId);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { width?: number; height?: number };
      if (typeof parsed.width === 'number' && typeof parsed.height === 'number') {
        storedSizeRef.current = { width: parsed.width, height: parsed.height };
      }
    } catch { /* localStorage unavailable */ }
  }, [enabled, storageId]);

  useEffect(() => {
    if (!ref.current) return;
    const root = ref.current;
    const scrollEl =
      (root.querySelector('[data-scroll-container]') as HTMLElement | null) ||
      (root.querySelector('.overflow-y-auto') as HTMLElement | null);
    if (!scrollEl) return;

    const updateHint = () => {
      const hasOverflow = scrollEl.scrollHeight > scrollEl.clientHeight + 1;
      const atBottom = scrollEl.scrollTop + scrollEl.clientHeight >= scrollEl.scrollHeight - 2;
      setShowScrollHint(hasOverflow && !atBottom);
    };

    updateHint();
    scrollEl.addEventListener('scroll', updateHint);
    const resizeObserver = new ResizeObserver(updateHint);
    resizeObserver.observe(scrollEl);

    return () => {
      scrollEl.removeEventListener('scroll', updateHint);
      resizeObserver.disconnect();
    };
  }, [enabled, size]);

  useEffect(() => {
    if (!ref.current) return;
    const root = ref.current;
    const markUnsaved = (event: Event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement)) {
        return;
      }
      if (target.getAttribute('data-ignore-unsaved') === 'true') return;
      if (!hasUnsavedRef.current) {
        hasUnsavedRef.current = true;
        root.setAttribute('data-unsaved', 'true');
      }
    };

    root.addEventListener('input', markUnsaved, true);
    root.addEventListener('change', markUnsaved, true);
    return () => {
      root.removeEventListener('input', markUnsaved, true);
      root.removeEventListener('change', markUnsaved, true);
    };
  }, []);

  useEffect(() => {
    if (!enabled) return;
    try {
      const raw = window.localStorage.getItem(baselineId);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { width?: number; height?: number };
      if (typeof parsed.width === 'number' && typeof parsed.height === 'number') {
        baselineSizeRef.current = { width: parsed.width, height: parsed.height };
        initialSizeRef.current = { width: parsed.width, height: parsed.height };
      }
    } catch { /* localStorage unavailable */ }
  }, [enabled, baselineId]);

  const resolveMaxValue = (value: string | number | undefined, axis: 'w' | 'h') => {
    if (typeof value === 'number') return value;
    if (!value) return axis === 'w' ? window.innerWidth - 32 : window.innerHeight - 32;
    if (value.includes('100vw')) return window.innerWidth - 32;
    if (value.includes('100vh')) return window.innerHeight - 32;
    return axis === 'w' ? window.innerWidth - 32 : window.innerHeight - 32;
  };

  const clampSize = (next: { width: number; height: number }) => {
    const maxW = resolveMaxValue(maxWidth, 'w');
    const maxH = resolveMaxValue(maxHeight, 'h');
    return {
      width: Math.min(Math.max(minWidth, next.width), maxW),
      height: Math.min(Math.max(minHeight, next.height), maxH),
    };
  };

  useEffect(() => {
    if (!enabled || !ref.current) return;

    let frameId: number | null = null;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const nextWidth = Math.round(entry.contentRect.width);
      const nextHeight = Math.round(entry.contentRect.height);
      if (nextWidth < minWidth || nextHeight < minHeight) return;

      if (resetPendingRef.current) {
        resetPendingRef.current = false;
        const baseline = { width: nextWidth, height: nextHeight };
        baselineSizeRef.current = baseline;
        initialSizeRef.current = baseline;
        const clamped = clampSize(baseline);
        setSize({ width: clamped.width, height: clamped.height });
        storedSizeRef.current = { width: clamped.width, height: clamped.height };
        try {
          window.localStorage.setItem(baselineId, JSON.stringify(baseline));
          window.localStorage.setItem(storageId, JSON.stringify(clamped));
        } catch { /* localStorage unavailable */ }
        return;
      }

      if (!hasMeasuredRef.current) {
        hasMeasuredRef.current = true;
        initialSizeRef.current = { width: nextWidth, height: nextHeight };
        if (!baselineSizeRef.current) {
          const baseline = { width: nextWidth, height: nextHeight };
          baselineSizeRef.current = baseline;
          try {
            window.localStorage.setItem(baselineId, JSON.stringify(baseline));
          } catch { /* localStorage unavailable */ }
        }

        const stored = storedSizeRef.current;
        if (stored) {
          const clamped = clampSize(stored);
          setSize({ width: clamped.width, height: clamped.height });
        }
        return;
      }

      if (isDraggingRef.current || hasUserSizedRef.current) return;
      lastMeasuredRef.current = { width: nextWidth, height: nextHeight };
    });

    observer.observe(ref.current);

    return () => {
      observer.disconnect();
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [enabled, minHeight, minWidth, storageId, clampSize]);

  const handlePointerDown: React.PointerEventHandler<HTMLDivElement> = (event) => {
    if (!enabled || !ref.current) return;
    event.preventDefault();
    event.stopPropagation();
    const rect = ref.current.getBoundingClientRect();
    isDraggingRef.current = true;
    suppressClickRef.current = true;
    if (showHint) {
      setShowHint(false);
      try {
        window.localStorage.setItem(HINT_KEY, 'true');
      } catch { /* localStorage unavailable */ }
    }
    dragStateRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      startWidth: rect.width,
      startHeight: rect.height,
    };

    const maxW = resolveMaxValue(maxWidth, 'w');
    const maxH = resolveMaxValue(maxHeight, 'h');

    const handlePointerMove = (moveEvent: PointerEvent) => {
      const state = dragStateRef.current;
      if (!state) return;
      const deltaX = moveEvent.clientX - state.startX;
      const deltaY = moveEvent.clientY - state.startY;
      const nextWidth = Math.min(
        Math.max(minWidth, state.startWidth + deltaX * 2),
        maxW
      );
      const nextHeight = Math.min(
        Math.max(minHeight, state.startHeight + deltaY * 2),
        maxH
      );
      setSize({ width: Math.round(nextWidth), height: Math.round(nextHeight) });
    };

    const handlePointerUp = () => {
      const state = dragStateRef.current;
      dragStateRef.current = null;
      isDraggingRef.current = false;
      hasUserSizedRef.current = true;
      const latestSize = latestSizeRef.current;
      if (state && latestSize) {
        try {
          window.localStorage.setItem(storageId, JSON.stringify(clampSize(latestSize)));
        } catch { /* localStorage unavailable */ }
      }
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'nwse-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);
  };

  const handleReset = () => {
    hasUserSizedRef.current = false;
    setSize(null);
    storedSizeRef.current = null;
    baselineSizeRef.current = null;
    initialSizeRef.current = null;
    hasMeasuredRef.current = false;
    resetPendingRef.current = true;
    try {
      window.localStorage.removeItem(storageId);
      window.localStorage.removeItem(baselineId);
    } catch { /* localStorage unavailable */ }
  };

  useEffect(() => {
    return () => {
      if (!enabled) return;
      const latest = latestSizeRef.current || lastMeasuredRef.current;
      if (!latest) return;
      const clamped = clampSize({
        width: Math.round(latest.width),
        height: Math.round(latest.height),
      });
      try {
        window.localStorage.setItem(storageId, JSON.stringify(clamped));
      } catch { /* localStorage unavailable */ }
    };
  }, [enabled, storageId, clampSize]);

  const mergedStyle: CSSProperties = {
    ...style,
    minWidth: enabled ? minWidth : style?.minWidth,
    minHeight: enabled ? minHeight : style?.minHeight,
    maxWidth: enabled ? maxWidth : style?.maxWidth,
    maxHeight: enabled ? maxHeight : style?.maxHeight,
    resize: undefined,
    width: enabled && size ? `${size.width}px` : style?.width,
    height: enabled && size ? `${size.height}px` : style?.height,
  };

  const marginMatch = (className || '').match(/\b((?:md:)?my?-\d+)\b/g);
  const outerMargin = marginMatch ? marginMatch.join(' ') : '';
  const innerClassName = (className || '')
    .replace(/\b(md:)?border\b/g, '')
    .replace(/\b(md:)?border-\[--c-border\]/g, '')
    .replace(/\b(md:)?rounded-(?:3xl|2xl)\b/g, '')
    .replace(/\b(md:)?overflow-hidden\b/g, '')
    .replace(/\b(md:)?shadow-xl\b/g, '')
    .replace(/\b(md:)?my?-\d+\b/g, '')
    .replace(/\b(md:)?max-h-\[[^\]]+\]/g, '')
    .replace(/\b(md:)?max-w-\[[^\]]+\]/g, '')
    .replace(/\b(md:)?max-h-\S+/g, '')
    .replace(/\b(md:)?max-w-\S+/g, '')
    .trim();

  return (
    <div
      ref={ref}
      role={role}
      aria-modal={ariaModal}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      className={`${enabled ? '' : 'h-[var(--cap4-modal-mobile-height,100dvh)] max-h-[var(--cap4-modal-mobile-height,100dvh)] min-h-0 w-full'} relative flex flex-col ${outerMargin}`}
      style={mergedStyle}
      data-resizable-modal-root="true"
      data-resizable-enabled={enabled ? 'true' : 'false'}
      data-unsaved="false"
    >
      <div
        className={`${enabled ? 'relative overflow-hidden rounded-2xl border border-[--c-border]' : 'relative'} flex flex-col flex-1 min-h-0 ${innerClassName || ''}`}
        {...innerRest}
      >
        {children}
        {enabled && (
          <div
            className="absolute bottom-1 right-1 h-6 w-6 cursor-nwse-resize z-50"
            aria-hidden="true"
            onPointerDown={handlePointerDown}
            onDoubleClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleReset();
            }}
          >
            <svg viewBox="0 0 24 24" className="absolute inset-0">
              <path d="M24 4 A20 20 0 0 1 4 24" fill="none" stroke="var(--c-muted)" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
        )}
        {showScrollHint && (
          <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-[--c-card] via-[--c-card]/30 to-transparent rounded-b-3xl" />
        )}
      </div>
      {enabled && showHint && (
        <div className="flex justify-end mt-2 pointer-events-none">
          <div className="whitespace-nowrap rounded-full bg-[--c-card]/90 px-3 py-1.5 text-[11px] font-medium text-[--c-muted] shadow-lg ring-1 ring-[--c-border] backdrop-blur-sm pointer-events-auto">
            {resizeHintText}
          </div>
        </div>
      )}
    </div>
  );
}
